// TabContent1.js
import { useTheme } from '@emotion/react';
import { Box, ButtonBase, InputBase, TextField, Typography } from '@mui/material';
import cx from "classnames";
import { t } from "i18next";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { useGetReturnReasonsSelectQuery } from '../../../../../newapi/inbound/returnsSlice';
import { useGetOrderLinesQuery } from '../../../../../newapi/order/ordersSlice';
import { tokens } from '../../../../../theme';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { StyledDropdown } from '../../../../theme/dropdowns/StyledDropdown';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import Subheading from '../../../../theme/text/Subheading';
import Text from '../../../../theme/text/Text';
import { errorMapWithArrayProcessor } from '../../../../../utils/helpers';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import ReturnLineLocation from '../../../shared/ReturnLineLocation';
import { ErrorBoundary } from 'react-error-boundary';



// The accepted and rejected counts, internal remark and booking location of one product on a
// return that is processed on the spot.
const ReturnLineCounts = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);

    return (
        <>
            <Box
                sx={{
                    display:"flex",
                    gap:2,
                }}
            >
                <Box
                    sx={{
                        display:"flex",
                        flexDirection:'column',
                        flex:1,
                        maxWidth:300,
                    }}
                >
                    <Text bold variant={"green"}>{t("accepted")}</Text>
                    <NumberInput
                        min={0}
                        max={props.qty}
                        value={props.rejectAll ? 0 : props.value?.acceptedCount}
                        disabled={props.rejectAll}
                        onlyPositive
                        change={props.onAcceptedChange}
                        inputSX={{
                            mt:1,
                            height:32,
                            borderRadius:4,
                            background:colors.green[100]
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display:"flex",
                        flexDirection:'column',
                        flex:1,
                        maxWidth:300,
                    }}
                >
                    <Text bold variant={"red"}>{t("rejected")}</Text>
                    <NumberInput
                        min={0}
                        max={props.qty}
                        value={props.rejectAll ? props.qty : props.value?.rejectedCount}
                        disabled={props.rejectAll}
                        onlyPositive
                        change={props.onRejectedChange}
                        inputSX={{
                            mt:1,
                            height:32,
                            borderRadius:4,
                            background:colors.red[100]
                        }}
                    />
                </Box>
            </Box>

            <InputBase
                multiline
                rows={4}
                value={props.value?.internalRemark}
                onChange={(e) => props.onInternalRemarkChange(e.target.value)}
                placeholder={t("internal_remark")}
                sx={{
                    mt:1,
                    width: '100%',
                    fontSize: 14,
                    px:2,
                    py:1.5,
                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                    borderRadius: 5,
                }}
            />

            {!props.rejectAll && parseInt(props.value?.acceptedCount) > 0 &&
                <ReturnLineLocation
                    productId={props.productId}
                    value={props.value}
                    fieldError={props.fieldError}
                    onChange={props.onLocationChange}
                />
            }
        </>
    )
}

const OrderDetailsContent = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);

    const [details, setDetails] = useState(null);

    // The counts stay behind a blur until the line is actually described.
    const requiredFilled = parseInt(props.data.qty) > 0 && parseInt(props.data.retourreasonId) > 0;

    // A virtual product is returned as one product, but its child products are judged and
    // booked one by one.
    const bundleChildren = details?.children ?? props.data.children ?? [];

    const handleActiveChange = (selected) => {
        if (!details.active) {
            handleAmountChange(props.amountOptions.length - 1);
        }

        setDetails(prevDetails => ({
            ...prevDetails,
            active: !prevDetails.active ?? false,
        }));
    }

    const handleReasonChange = (selected) => {
        setDetails(prevDetails => ({
            ...prevDetails,
            retourreasonId: selected
        }));
    }

    const handleAmountChange = (selected) => {
        setDetails(prevDetails => {
            const qty = props.amountOptions[parseInt(selected)].name;
            return {
                ...prevDetails,
                qty: qty,
                // Default to everything accepted - the common case - and keep the
                // pair summing to qty from the moment an amount exists. An unsellable
                // return is the exception: none of it can go back into stock.
                ...(props.processImmediately
                    ? props.rejectAll
                        ? { acceptedCount: 0, rejectedCount: qty }
                        : { acceptedCount: qty, rejectedCount: 0 }
                    : {}),
                ...(props.processImmediately && prevDetails.children?.length
                    ? {
                        children: prevDetails.children.map(child => {
                            const childQty = qty * child.perBundle;
                            return {
                                ...child,
                                acceptedCount: props.rejectAll ? 0 : childQty,
                                rejectedCount: props.rejectAll ? childQty : 0,
                            };
                        })
                    }
                    : {}),
            };
        });
    }

    // The virtual product counts as accepted only as far as every child product is accepted
    // for it; the rest of the returned amount counts as rejected.
    const withBundleCounts = (bundle) => {
        const qty = parseInt(bundle.qty) || 0;
        const accepted = Math.min(qty, ...bundle.children.map(child =>
            Math.floor((parseInt(child.acceptedCount) || 0) / child.perBundle)
        ));
        return {
            ...bundle,
            acceptedCount: accepted,
            rejectedCount: qty - accepted,
        };
    }

    const handleChildChange = (index, updates) => {
        setDetails(prevDetails => withBundleCounts({
            ...prevDetails,
            children: prevDetails.children.map((child, i) => i === index ? { ...child, ...updates } : child),
        }));
    }

    // Accepted and rejected of a child product always sum to its share of the returned amount.
    const handleChildAcceptedChange = (index, amount) => {
        const childQty = (parseInt(details?.qty) || 0) * details.children[index].perBundle;
        const accepted = Math.min(Math.max(parseInt(amount) || 0, 0), childQty);
        handleChildChange(index, { acceptedCount: accepted, rejectedCount: childQty - accepted });
    }

    const handleChildRejectedChange = (index, amount) => {
        const childQty = (parseInt(details?.qty) || 0) * details.children[index].perBundle;
        const rejected = Math.min(Math.max(parseInt(amount) || 0, 0), childQty);
        handleChildChange(index, { rejectedCount: rejected, acceptedCount: childQty - rejected });
    }

    // Accepted and rejected always sum to qty, so entering one derives the other.
    const handleAcceptedChange = (amount) => {
        setDetails(prevDetails => {
            const qty = parseInt(prevDetails.qty) || 0;
            const accepted = Math.min(Math.max(parseInt(amount) || 0, 0), qty);
            return {
                ...prevDetails,
                acceptedCount: accepted,
                rejectedCount: qty - accepted,
            };
        });
    }

    const handleRejectedChange = (amount) => {
        setDetails(prevDetails => {
            const qty = parseInt(prevDetails.qty) || 0;
            const rejected = Math.min(Math.max(parseInt(amount) || 0, 0), qty);
            return {
                ...prevDetails,
                rejectedCount: rejected,
                acceptedCount: qty - rejected,
            };
        });
    }

    const handleInputChange = (e,val) => {
        setDetails(prevDetails => ({
            ...prevDetails,
            remark: val
        }));
    }

    const handleInternalRemarkChange = (val) => {
        setDetails(prevDetails => ({
            ...prevDetails,
            internalRemark: val
        }));
    }

    const handleLocationChange = (updates) => {
        setDetails(prevDetails => ({
            ...prevDetails,
            ...updates,
        }));
    }

    useEffect(() => {
        if (!details) {
            setDetails(props.data);
        }
    }, [props.data]);

    useEffect(() => {
        
        if(details)
            props.onReturnChange(details);
    }, [details]);

    return (
        <Box
            className={cx('order-inner-content',{
                'active': props.active,
                'cantbereturned': props.cantbereturned
            })}
            TouchRippleProps={{
                style: {
                    color: "#31415f",
                }
            }}
            sx={{
                padding:1.5,
                borderRadius:4,
                position:"relative",
                
                opacity:1,
                display:"flex",
                flexDirection:"column",
                alignItems:"flex-start",
                background:colors.grey[100],
                "&.active": {
                    opacity:1,
                    background:colors.grey[50],
                },
                "&.cantbereturned" : {
                    opacity:0.5,
                }
            }}
        >
            <ButtonBase
                onClick={handleActiveChange}
                component="div"
                TouchRippleProps={{
                    style: {
                        color: "#31415f",
                    }
                }}
                sx={{
                    pr:8,
                }}
            >
                <Text fontWeight={props.active ? 500 : 400} bold allowOverflow>
                    {props.title}
                </Text>
            </ButtonBase>

            {/* Dropdowns */}
            {props.active &&
            <Box
                sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:2,
                    width:1,
                    mt:3,
                }}
            >
                <Box>
                    <Text>Aantal</Text>
                    <StyledDropdown
                        closeOnSelection
                        options={props.amountOptions}
                        selected={props.amountOptions.find(option => option.name === props.data.qty)?.id ?? -1}
                        onChange={(selected) => {
                            handleAmountChange(selected)
                        }}
                        disallowDeselection
                        noSelection={t("choose_amount")}
                        multiple={false}
                        displayName={"name"}
                        valueKey={"id"}
                        sx={{
                            base: {
                                background:props.fieldError !== "undefined" && typeof props.fieldError?.qty !== "undefined" ? colors.red[100] : "#ecf0f5",
                                paddingRight:0.5,
                                mt:1,
                                fontSize:14,
                                // color:"#31415f"
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.qty !== "undefined" ? colors.red[400] : "#31415f",

                            },
                            text: {
                                fontSize:14,
                                // color:"#31415f"
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.qty !== "undefined" ? colors.red[400] : "#31415f",

                            },
                            button: {
                                background:props.fieldError !== "undefined" && typeof props.fieldError?.qty !== "undefined" ? colors.red[100] : "#ecf0f5",
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.qty !== "undefined" ? colors.red[400] : "#31415f",
                            },
                            touchProps: {
                                style: { 
                                    color: "#31415f"
                                } 
                            }
                        }}
                    />
                </Box>

                <Box>
                    <Text>{t("reason")}</Text>
                    <StyledDropdown
                        key={props.data.retourreasonId}
                        closeOnSelection
                        onChange={(selected) => handleReasonChange(selected)}
                        options={props.reasonOptions}
                        selected={props.reasonOptions.find(option => option.id === props.data.retourreasonId)?.id ?? -1}
                        displayName={"name"}
                        noSelection={t("select_a_reason")}
                        multiple={false}
                        valueKey={"id"}
                        disallowDeselection
                        sx={{
                            base: {
                                background:props.fieldError !== "undefined" && typeof props.fieldError?.retourreasonId !== "undefined" ? colors.red[100] : "#ecf0f5",
                                paddingRight:0.5,
                                mt:1,
                                fontSize:14,
                                // color:"#31415f"
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.retourreasonId !== "undefined" ? colors.red[400] : "#31415f",

                            },
                            text: {
                                fontSize:14,
                                // color:"#31415f"
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.retourreasonId !== "undefined" ? colors.red[400] : "#31415f",

                            },
                            button: {
                                background:props.fieldError !== "undefined" && typeof props.fieldError?.retourreasonId !== "undefined" ? colors.red[100] : "#ecf0f5",
                                color:props.fieldError !== "undefined" && typeof props.fieldError?.retourreasonId !== "undefined" ? colors.red[400] : "#31415f",
                            },
                            touchProps: {
                                style: { 
                                    color: "#31415f"
                                } 
                            }
                        }}
                    />
                </Box>

                <Box>
                    <Text>{t("remark")}</Text>
                    
                    <TextField

                        id="outlined-multiline-static"
                        multiline
                        value={props.data?.remark}
                        onChange={(event) => handleInputChange(event, event.target.value)}
                        rows={3}
                        variant="outlined"
                        sx={{
                            background:"#ecf0f5",
                            borderRadius:3,
                            width:1,
                            border:"none",
                            outlineColor:"transparent !important",
                            color: "#31415f",
                            padding:0,
                            " .MuiOutlinedInput-notchedOutline": {
                                border:"none !important"
                            },
                            " .MuiInputBase-root": {
                                paddingX:2,
                                paddingY:1.5
                            }
                        }}

                    ></TextField>
                </Box>

                {props.processImmediately &&
                <Box
                    sx={{
                        p:1.5,
                        position:"relative",
                        borderRadius:4,
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                    }}
                >
                    {!requiredFilled &&
                        <Box
                            sx={{
                                position:"absolute",
                                top:0,
                                left:0,
                                right:0,
                                bottom:0,
                                zIndex:1,
                                borderRadius:4,
                                backdropFilter:"blur(16px)",
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                            }}
                        >
                            <Text bold>{t("fill_in_required_fields_first")}</Text>
                        </Box>
                    }
                    {bundleChildren.length > 0 ?
                        bundleChildren.map((child, index) => {
                            const childQty = (parseInt(props.data.qty) || 0) * child.perBundle;
                            return (
                                <Box
                                    key={child.productId}
                                    sx={{
                                        mt: index > 0 ? 3 : 0,
                                    }}
                                >
                                    <Text bold>{child.title} ({childQty} {t("pcs")})</Text>
                                    <ReturnLineCounts
                                        qty={childQty}
                                        value={child}
                                        productId={child.productId}
                                        rejectAll={props.rejectAll}
                                        fieldError={props.childFieldErrors?.[index]}
                                        onAcceptedChange={(amount) => handleChildAcceptedChange(index, amount)}
                                        onRejectedChange={(amount) => handleChildRejectedChange(index, amount)}
                                        onInternalRemarkChange={(val) => handleChildChange(index, { internalRemark: val })}
                                        onLocationChange={(updates) => handleChildChange(index, updates)}
                                    />
                                </Box>
                            );
                        })
                    :
                        <ReturnLineCounts
                            qty={parseInt(props.data.qty) || 0}
                            value={details ?? props.data}
                            productId={props.data.productId}
                            rejectAll={props.rejectAll}
                            fieldError={props.fieldError}
                            onAcceptedChange={handleAcceptedChange}
                            onRejectedChange={handleRejectedChange}
                            onInternalRemarkChange={handleInternalRemarkChange}
                            onLocationChange={handleLocationChange}
                        />
                    }
                </Box>
                }
            </Box>
            }



            {/* Switch */}
            <Box
                sx={{
                    position:'absolute',
                    right:0,
                    top:0,
                }}
            >
                <ShSwitch onChange={handleActiveChange} checked={props.active}/>
            </Box>
        </Box>
    )
}
const OrderCantBeReturnedDetailsContent = (props) => {
 
    return (
        <>
        <Box
                className={cx('order-inner-content',{
                    'active': props.active,
                    'cantbereturned': props.cantbereturned
                })}
                sx={{
                    padding:1.5,
                    borderRadius:4,
                    position:"relative",
                    pr:7,
                    
                    opacity:1,
                    "&.cantbereturned" : {
                        opacity:0.55,
                    }
                }}
            >
                <Subheading allowOverflow fontWeight={400}>
                    {props.title}
                </Subheading>

            </Box>

            <Box
                sx={{
                    pl:1.5,
                    pb:1.5,
                    mt:"-8px",
                }}
            >
                <Typography
                    variant="span"
                    sx={{
                        color:"rgba(49,65,95,1)",
                        fontSize:16,
                        fontWeight:500,
                    }}
                >
                {t("item_cant_be_returned")}
                </Typography>
            </Box>
        </>
    )
}

const OrderDetails = (props) => {
    
    const amountOptions = [{id: -1, name: "Kies een aantal"}, ...Array.from({length: props.qty }, (_, index) => ({id: index +1 , name: index +1}))];
    const reasonOptions = [{id: -1, name: "Kies een reden"}, ...props.reasons?.map(reason => ({id: reason.id, name: reason.description}))];

    return (
        
        <Box
            className={cx({
                'active': props.data.active
            })}
            sx={{
            }}
        >
            {typeof props.cantbereturned === "undefined" || !props.cantbereturned ?
                <OrderDetailsContent
                    fieldError={props.fieldError}
                    childFieldErrors={props.childFieldErrors}
                    active={props.data.active}
                    // onClick={onChange}
                    cantbereturned={props.cantbereturned}
                    title={props.title}
                    amountOptions={amountOptions}
                    reasonOptions={reasonOptions}
                    id={props.data?.id}
                    data={props.data}
                    processImmediately={props.processImmediately}
                    rejectAll={props.rejectAll}
                    onReturnChange={props.onReturnChange}
                />
            :
                <OrderCantBeReturnedDetailsContent
                    active={props.data.active}
                    cantbereturned={props.cantbereturned}
                    title={props.title}
                />
                // null
            }
        </Box>
    )
}

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
// The products a virtual product order line is made of. A child that is itself a virtual
// product is replaced by its own children, so only products that are put away remain.
const bundleChildLines = (orderline) => (orderline.children ?? []).flatMap(child =>
    child.children?.length ? bundleChildLines(child) : [child]
);

const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.localData };
    const dispatch = useDispatch();
    const {data: reasons, isLoading:returnReasonsLoading,isFetching:returnReasonsFetching} = useGetReturnReasonsSelectQuery({relationId:data.relationId});
    const {data: lines,isSuccess,isError} = useGetOrderLinesQuery(
        {
            args: {
                id: props.localData?.orderId,
                page:1,
                size:200,
            }
        },
        {
            skip: !props.localData?.orderId
        }
    );

    const [returnRequest, setReturnRequest] = useState({
        returnLines: []
    })
    const [validationErrors, setValidationErrors] = useState({});
    
   
    useEffect(() => {
        if (isSuccess && lines && lines.data ) {
            // console.log([...props.localData?.returnLines]);
            const newItems = props.localData?.returnLines?.length
            ? [...props.localData.returnLines] // Use the returnLines from props.localData if they exist
            : lines.data.map(orderline => ({
                id:orderline.id,
                productId: orderline.product.id,
                active: false,
                qty: -1,
                retourreasonId: -1,
                remark: "",
                acceptedCount: "",
                rejectedCount: "",
                internalRemark: "",
                warehouseLocationId: null,
                chosenLocation: null,
                selectedWarehouseLocationIndex: -1,
                children: bundleChildLines(orderline).map(child => ({
                    productId: child.product.id,
                    title: child.product.description,
                    perBundle: child.amount / orderline.amount,
                    acceptedCount: "",
                    rejectedCount: "",
                    internalRemark: "",
                    warehouseLocationId: null,
                    chosenLocation: null,
                    selectedWarehouseLocationIndex: -1,
                })),
            }));

            
            setReturnRequest(prevState => ({
                ...prevState,
                returnLines: newItems
            }));
        }
    }, [lines, isSuccess, isError]);    
    
    const handleErrors = (errors) => {
        const { fieldErrors, generalErrors } = errorMapWithArrayProcessor(errors, /returnLines\[(\d+)\]\.(qty|retourreasonId|warehouseLocationId)/);

        // Errors on the child products of a virtual product, per line and per child.
        const childFieldErrors = {};
        Object.keys(errors ?? {}).forEach(errorKey => {
            const match = errorKey.match(/returnLines\[(\d+)\]\.children\[(\d+)\]\.(\w+)/);
            if (match) {
                const [, lineIndex, childIndex, field] = match;
                childFieldErrors[lineIndex] = childFieldErrors[lineIndex] ?? {};
                childFieldErrors[lineIndex][childIndex] = { ...childFieldErrors[lineIndex][childIndex], [field]: errors[errorKey] };
            }
        });

        setValidationErrors({ fieldErrors, generalErrors, childFieldErrors });
      };

    const onReturnItemChange = (item) => {
        if(!item) return;
        setReturnRequest(prevState => {
            const existingItemIndex = prevState.returnLines.findIndex(i => i.id === item.id);
            const updatedItems = [...prevState.returnLines];
            updatedItems[existingItemIndex] = item;
            return { returnLines: updatedItems };
        });
    }

    useEffect(() => {
        dispatch(setTabErrors({ }));
        props.setLocalData({
            ...props.localData,
            returnLines: returnRequest.returnLines.filter(item => item.active === true) // Filter and spread the active items
        })
    }, [returnRequest])

    useEffect(() => {
        handleErrors(props.errors);
    }, [props.errors])

    return (
        <Box
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                borderTopLeftRadius:16,
                borderTopRightRadius:16,
                display:"flex",
                flexDirection:"column",
                gap:1,
            }}
        >
            {validationErrors.generalErrors && Object.keys(validationErrors.generalErrors).map((errorKey) => (
                errorKey === "returnLines" &&
                <Box 
                    sx={{
                        height:32,
                        width:1,
                        textAlign:"center",
                        lineHeight:"32px",
                        borderRadius:4,
                        border:`1px solid ${colors.red[400]}`,
                        background:colors.red[100],
                        mb:2,
                    }}
                >
                    <Text variant="red" key={errorKey}>{t(validationErrors.generalErrors[errorKey])}</Text>
                </Box>
            ))}

            <ErrorBoundary
                FallbackComponent={() => <div>Er is iets misgegaan tijdens het inladen. Probeer eens op terug te klikken en dan weer op volgende</div>}
                onError={(error, info) => console.error(error, info)}
            >
                {(!returnReasonsLoading && !returnReasonsFetching) && returnRequest?.returnLines.map((item, index) => (
                    <OrderDetails 
                        fieldError={validationErrors.fieldErrors[index]}
                        childFieldErrors={validationErrors.childFieldErrors?.[index]}
                        key={item.id}
                        id={item.id} 
                        title={lines.data[index]?.product?.description} 
                        qty={lines.data[index]?.amount} 
                        reasons={reasons} 
                        cantbereturned={lines.data[index].product.returnable}
                        data={item}
                        processImmediately={data.processImmediately}
                        rejectAll={data.rejectAll}
                        onReturnChange={onReturnItemChange}
                    />
                ))}
            </ErrorBoundary>

            
        </Box>
    );
};


const ReturnLines = (props) => {
    const { t } = useTranslation();

    const data = useSelector((state) => state.progressiontabs.data);
    const processImmediately = data.processImmediately ?? false;

    // The location rule for a line and for each child product of a virtual product.
    const locationSchema = yup.number().nullable().test(
      'location-required',
      'choose_a_location_to_book_the_items_onto',
      function (value) {
        const { hasReturnLocation, acceptedCount } = this.parent;
        if (hasReturnLocation === false && parseInt(acceptedCount) > 0) {
          return typeof value === 'number' && value > 0;
        }
        return true;
      }
    );

    const conditionsSchema = yup.array().of(
        yup.object({
          qty: yup.number()
          .moreThan(0, 'amount_must_be_greater_than_0')
            .required("amount_required"),
          // Settling a return on the spot means recording why and how much,
          // so both become mandatory that the announce-only flow leaves optional.
          ...(processImmediately ? {
            retourreasonId: yup.number()
              .moreThan(0, 'select_a_reason')
              .required('select_a_reason'),
            acceptedCount: yup.number()
              .min(0, 'accepted_count_must_be_0_or_more')
              .required('accepted_count_required'),
            rejectedCount: yup.number()
              .min(0, 'rejected_count_must_be_0_or_more')
              .required('rejected_count_required'),
            // Accepting stock onto a product with no home requires binding a location.
            warehouseLocationId: locationSchema,
            // The child products of a virtual product are judged and booked one by one.
            children: yup.array().of(
              yup.object({
                acceptedCount: yup.number()
                  .min(0, 'accepted_count_must_be_0_or_more')
                  .required('accepted_count_required'),
                rejectedCount: yup.number()
                  .min(0, 'rejected_count_must_be_0_or_more')
                  .required('rejected_count_required'),
                warehouseLocationId: locationSchema,
              })
            ),
          } : {}),
        })
      ).min(1, 'conditions_not_valid');

    const schema = yup.object({
        returnLines: conditionsSchema
      });

    const [localData, setLocalData] = useState(data);

    useEffect(() => {
        setLocalData(data);
    },[data]);

    const handleBeforeSave = (dataToSave) => {
        if (!processImmediately || !dataToSave.rejectAll) {
            return dataToSave;
        }

        // An unsellable return rejects everything, so the per-line counts follow the
        // return rather than whatever was last shown.
        const result = {
            ...dataToSave,
            returnLines: dataToSave.returnLines.map(line => ({
                ...line,
                acceptedCount: 0,
                rejectedCount: parseInt(line.qty) || 0,
                children: (line.children ?? []).map(child => ({
                    ...child,
                    acceptedCount: 0,
                    rejectedCount: (parseInt(line.qty) || 0) * child.perBundle,
                })),
            })),
        };
        setLocalData(result);
        return result;
    };

    return (
        <ProgressionTab
            tabId="tabReturnLines"
            subtitle={t("return_lines")}
            // nextTab={"tabShipments"}
            step={2}
            maxSteps={2}
            schema={schema}
            updateDataWithValidation={localData} //updates pre-tab-switch
            onBeforeSave={handleBeforeSave}
        >
            <Content localData={localData} setLocalData={setLocalData}/>
        </ProgressionTab>
    )
}

export default ReturnLines;
